import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ENV } from '../config/env';
import type { Product } from '../types';

interface ProductState {
  products: Product[];
  categories: string[];
  featuredProducts: Product[];
  promoProducts: Product[];
  bannerProducts: Product[];
  loading: boolean;
  error: string | null;
  loadProducts: () => Promise<void>;
  getProductById: (id: string) => Product | undefined;
  getProductsByCategory: (category: string) => Product[];
  searchProducts: (query: string) => Product[];
}

export const useProductStore = create<ProductState>()(
  persist(
    (set, get) => ({
      products: [],
      categories: [],
      featuredProducts: [],
      promoProducts: [],
      bannerProducts: [],
      loading: false,
      error: null,
      loadProducts: async () => {
        set({ loading: true, error: null });
        try {
          const res = await fetch(`${ENV.API_BASE}/products`);
          const data = await res.json();
          const products: Product[] = Array.isArray(data) ? data : data.products || [];
          const sortedProducts = [...products].sort((a, b) => {
            if (a.featured && !b.featured) return -1;
            if (!a.featured && b.featured) return 1;
            const aOrder = a.bannerOrder ?? 999;
            const bOrder = b.bannerOrder ?? 999;
            if (aOrder !== bOrder) return aOrder - bOrder;
            return a.name.localeCompare(b.name);
          });
          const categories = [...new Set(products.map((product) => product.category))];
          const featuredProducts = sortedProducts.filter((product) => product.featured);
          const promoProducts = sortedProducts.filter((product) => product.promo);
          const bannerProducts = sortedProducts.filter((product) => product.banner);

          set({ products: sortedProducts, categories, featuredProducts, promoProducts, bannerProducts, loading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to load products',
            loading: false,
          });
        }
      },
      getProductById: (id) => get().products.find((product) => product.id === id),
      getProductsByCategory: (category) => {
        if (category === 'all') return get().products;
        return get().products.filter((product) => product.category === category);
      },
      searchProducts: (query) => {
        const normalizedQuery = query.toLowerCase();
        return get().products.filter(
          (product) =>
            product.name.toLowerCase().includes(normalizedQuery) ||
            product.category.toLowerCase().includes(normalizedQuery) ||
            (product.description || '').toLowerCase().includes(normalizedQuery) ||
            product.specs.some((spec) => spec.toLowerCase().includes(normalizedQuery)),
        );
      },
    }),
    { name: 'tc-products-v3' },
  ),
);
